// frontend/app/components/discoveryJourneys/JourneyMissionHeader.js
//
// The head of the journey screen: the compelling question the student came for,
// the mission set-up that makes it worth answering, and the author's own
// statements of what will be mastered and what success looks like.
//
// The document's point in one component — the student sees "Can you design the
// fastest Rescue Drone?", not "Chapter 2: Motion and Kinematics". The curriculum
// mapping is still here, but as a breadcrumb, not as the headline.

import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { tint } from "../lessonPlans/lessonPlanTheme";
import { fill } from "./journeyProgress";

/** A titled list of authored sentences — objectives, success criteria. */
const MissionList = ({ items, icon, heading, colors, testID, t }) => {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <View testID={testID} style={styles.missionList}>
      <Text style={[styles.missionListHeading, { color: colors.textSecondary }]}>
        {t(heading)}
      </Text>
      {list.map((item, index) => (
        <View key={`${index}-${String(item).slice(0, 24)}`} style={styles.missionListRow}>
          <MaterialCommunityIcons
            name={icon}
            size={14}
            color={colors.accentPrimary}
            style={styles.missionListIcon}
          />
          <Text style={[styles.missionListText, { color: colors.textSecondary }]}>{item}</Text>
        </View>
      ))}
    </View>
  );
};

const JourneyMissionHeader = ({ journey, progress, colors, isDarkMode, t }) => {
  const mission = String(journey?.mission_story || "").trim();
  const subtitle = String(journey?.subtitle || "").trim();
  const breadcrumb = [journey?.subject_name, journey?.chapter_name]
    .filter(Boolean)
    .join("  ›  ");

  return (
    <View
      testID="journey-mission"
      style={[
        styles.mission,
        {
          backgroundColor: colors.panelBg,
          borderColor: colors.border,
          borderLeftColor: colors.accentPrimary,
        },
      ]}
    >
      <View style={styles.missionTop}>
        <View
          style={[
            styles.missionIcon,
            { backgroundColor: tint(colors.accentPrimary, isDarkMode, 2.4) },
          ]}
        >
          <MaterialCommunityIcons
            name="map-marker-path"
            size={18}
            color={colors.accentPrimary}
          />
        </View>
        <View style={styles.missionTopText}>
          <Text style={[styles.eyebrow, { color: colors.accentPrimary }]}>
            {t("Discovery Journey")}
          </Text>
          {breadcrumb ? (
            <Text style={[styles.breadcrumb, { color: colors.textMuted }]} numberOfLines={1}>
              {breadcrumb}
            </Text>
          ) : null}
        </View>
        {progress.total > 0 ? (
          <Text
            testID="journey-progress"
            style={[styles.missionProgress, { color: colors.textSecondary }]}
          >
            {progress.isComplete
              ? t("Journey complete")
              : fill(t("Loop {current} of {total}"), {
                  current: progress.current || 1,
                  total: progress.total,
                })}
          </Text>
        ) : null}
      </View>

      {/* The compelling question. The document's whole point: the student sees
          a question, not "Chapter 2: Motion and Kinematics". */}
      <Text style={[styles.question, { color: colors.textPrimary }]}>{journey?.title}</Text>
      {subtitle ? (
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{subtitle}</Text>
      ) : null}
      {mission ? (
        <Text
          testID="journey-mission-story"
          style={[styles.missionStory, { color: colors.textSecondary }]}
        >
          {mission}
        </Text>
      ) : null}

      <MissionList
        testID="journey-objectives"
        items={journey?.objectives}
        icon="target"
        heading="What you will master"
        colors={colors}
        t={t}
      />
      <MissionList
        testID="journey-success-criteria"
        items={journey?.success_criteria}
        icon="flag-checkered"
        heading="You will know you are there when"
        colors={colors}
        t={t}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mission: {
    borderRadius: 16,
    borderWidth: 1,
    borderLeftWidth: 3,
    padding: 18,
    marginBottom: 18,
  },
  missionTop: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 10 },
  missionIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  missionTopText: { flex: 1, minWidth: 0 },
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  breadcrumb: { fontSize: 11, marginTop: 2 },
  missionProgress: { fontSize: 12, fontWeight: "700" },
  question: { fontSize: 22, fontWeight: "800", lineHeight: 29 },
  subtitle: { fontSize: 14, fontWeight: "600", lineHeight: 20, marginTop: 6 },
  missionStory: { fontSize: 13, lineHeight: 20, marginTop: 10 },

  missionList: { marginTop: 14 },
  missionListHeading: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  missionListRow: { flexDirection: "row", alignItems: "flex-start", gap: 6, marginBottom: 4 },
  missionListIcon: { marginTop: 2 },
  missionListText: { flex: 1, fontSize: 13, lineHeight: 19, marginLeft: 6 },
});

export default JourneyMissionHeader;
